/**
 * Public Brochure Viewer — Pollen Dots
 *
 * Brand source of truth taken from pollendots.com HTML:
 *   • Background:  #FCF8F7   (warm cream paper)
 *   • Primary ink: #030922   (deep navy)
 *   • Muted ink:   #787878
 *   • Sage/Pine:   #163224
 *   • Maroon:      #481220
 *   • Coral petal: #F3BAA8
 *   • Serif:       'Larken' (fallback Cormorant / Playfair / Georgia)
 *   • Mono / CTA:  'JetBrains Mono', monospace
 *
 * The title is rendered in Larken with the browser's natural kerning —
 * no artificial letter-spacing tricks. Media keeps its native aspect
 * ratio via object-contain.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Share2, VolumeX, Volume2, Play } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const SITE = 'https://pollendots.com';

// Brand palette — mirrors pollendots.com exactly.
const C = {
  bg: '#FCF8F7',
  ink: '#030922',
  muted: '#787878',
  pine: '#163224',
  maroon: '#481220',
  petal: '#F3BAA8',
  gold: '#B4823B',
};

// Inline pollen petal mark from the website's compass SVG — six petals
// arranged in a flower, with a soft maroon center.
const PetalDivider = () => (
  <svg width="64" height="36" viewBox="0 0 24 24" fill="none" aria-hidden="true"
       className="mx-auto opacity-90">
    {[0, 60, 120, 180, 240, 300].map(deg => (
      <ellipse key={deg} cx="12" cy="4.5" rx="1.4" ry="4.2" fill={C.petal}
               transform={`rotate(${deg} 12 12)`} />
    ))}
    <circle cx="12" cy="12" r="1.4" fill={C.maroon} />
  </svg>
);

// Three flowers in a row — used above each hotel section to give a
// quiet, deliberate break between chapters. Tighter than the trailing
// PetalDivider so it reads as a section opener, not a closer.
const TripleFlowerDivider = () => (
  <div className="flex items-center justify-center gap-5 py-5 sm:py-6"
       aria-hidden="true" data-testid="brochure-flower-divider">
    {[0, 1, 2].map(i => (
      <svg key={i} width="22" height="22" viewBox="0 0 24 24" fill="none">
        {[0, 60, 120, 180, 240, 300].map(deg => (
          <ellipse key={deg} cx="12" cy="4.5" rx="1.4" ry="4.2" fill={C.petal}
                   transform={`rotate(${deg} 12 12)`} />
        ))}
        <circle cx="12" cy="12" r="1.4" fill={C.maroon} />
      </svg>
    ))}
  </div>
);

// Tagline — Travel • Connect • Care — matches the wordmark color and
// weight used on pollendots.com (deep maroon serif, title case, coral
// pollen-dot separators). Sits directly under the wordmark portion of
// the combined logo (NOT under the emblem) as ONE brand mark.
const Tagline = ({ compact = false }) => (
  <p className={`flex items-center justify-center ${compact ? 'gap-1.5 text-[10px] sm:text-xs' : 'gap-2 sm:gap-3 text-sm sm:text-base'}`}
     style={{ color: C.maroon, fontWeight: 500, letterSpacing: '0.02em' }}>
    <span>Travel</span>
    <span className={`inline-block rounded-full ${compact ? 'w-1 h-1' : 'w-1.5 h-1.5'}`} style={{ background: '#E8B53D' }} />
    <span>Connect</span>
    <span className={`inline-block rounded-full ${compact ? 'w-1 h-1' : 'w-1.5 h-1.5'}`} style={{ background: '#E8B53D' }} />
    <span>Care</span>
  </p>
);

// Sticky top brand banner. The combined logo PNG has the circular emblem
// on the LEFT (~22% of its width) and the "POLLEN DOTS" wordmark on the
// RIGHT (~78%). We shift the tagline right by that ratio so it visually
// centres under the wordmark — not under the page.
const BrandBanner = () => (
  <header className="sticky top-0 z-40 backdrop-blur-md"
          style={{ background: `${C.bg}EE`, borderBottom: `1px solid ${C.petal}66` }}>
    <div className="max-w-5xl mx-auto px-4 py-3 sm:py-4 flex justify-center">
      <a href={SITE} target="_blank" rel="noreferrer"
         className="inline-block leading-none text-left"
         data-testid="brochure-brand-banner">
        <img src={`${SITE}/assets/images/header/pollen.png`} alt="Pollen Dots"
             className="block h-6 sm:h-7 w-auto" />
        <div className="pl-[22%] mt-1">
          <Tagline compact />
        </div>
      </a>
    </div>
  </header>
);

const _vid = (url, kind) => {
  if (!url) return '';
  if (kind === 'youtube') {
    const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([\w-]{6,})/);
    return m ? m[1] : '';
  }
  if (kind === 'vimeo') {
    const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    return m ? m[1] : '';
  }
  return '';
};

// Single media tile — keeps native aspect ratio. No cropping.
// YouTube + Vimeo render as a clean click-to-play poster with our own
// play button. The YouTube chrome only appears AFTER the viewer taps,
// keeping the brochure feel intact on first load.
const Media = ({ m, eager = false, onVideoPlay, onVideoPause }) => {
  const [embedded, setEmbedded] = useState(false);
  const cap = m.caption && (
    <figcaption className="text-xs italic mt-3 text-center px-4" style={{ color: C.muted }}>
      {m.caption}
    </figcaption>
  );
  const playOverlay = (poster) => (
    <button type="button"
            onClick={() => { setEmbedded(true); onVideoPlay && onVideoPlay(); }}
            className="relative w-full block group"
            style={{ paddingTop: '56.25%' }}>
      {poster && (
        <img src={poster} alt=""
             className="absolute inset-0 w-full h-full object-cover" />
      )}
      <span className="absolute inset-0 flex items-center justify-center bg-black/15 group-hover:bg-black/25 transition">
        <span className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center shadow-2xl"
              style={{ background: C.bg, color: C.maroon }}>
          <Play className="w-7 h-7 sm:w-8 sm:h-8" fill="currentColor" />
        </span>
      </span>
    </button>
  );

  const _src = (u) => /^https?:\/\//i.test(u) ? u : `${API}${u}`;

  if (m.kind === 'photo') {
    return (
      <figure>
        <img src={_src(m.url)} alt={m.caption || ''}
             loading={eager ? 'eager' : 'lazy'}
             className="w-full h-auto block" />
        {cap}
      </figure>
    );
  }
  if (m.kind === 'video') {
    return (
      <figure>
        <video src={_src(m.url)} controls playsInline preload="metadata"
               onPlay={onVideoPlay} onPause={onVideoPause} onEnded={onVideoPause}
               className="w-full h-auto block" />
        {cap}
      </figure>
    );
  }
  if (m.kind === 'youtube') {
    const id = _vid(m.url, 'youtube');
    return (
      <figure>
        {!embedded
          ? playOverlay(`https://i.ytimg.com/vi/${id}/maxresdefault.jpg`)
          : (
            <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
              <iframe title={m.caption || 'video'} className="absolute inset-0 w-full h-full"
                      src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&modestbranding=1&rel=0&iv_load_policy=3&playsinline=1`}
                      allow="autoplay; encrypted-media; picture-in-picture"
                      allowFullScreen />
            </div>
          )}
        {cap}
      </figure>
    );
  }
  if (m.kind === 'vimeo') {
    const id = _vid(m.url, 'vimeo');
    return (
      <figure>
        {!embedded
          ? playOverlay(`https://vumbnail.com/${id}.jpg`)
          : (
            <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
              <iframe title={m.caption || 'video'} className="absolute inset-0 w-full h-full"
                      src={`https://player.vimeo.com/video/${id}?autoplay=1&title=0&byline=0&portrait=0`}
                      allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
            </div>
          )}
        {cap}
      </figure>
    );
  }
  return null;
};

// Inline social icons (no external dep beyond lucide).
const ICON = {
  whatsapp: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.821 11.821 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.554-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.687-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.738-.607zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413z"/>
    </svg>
  ),
  instagram: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" />
    </svg>
  ),
  email: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  ),
  globe: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20" />
    </svg>
  ),
};

const Social = ({ href, icon, label }) => (
  <a href={href} target="_blank" rel="noreferrer"
     className="flex flex-col items-center gap-1 px-3 py-2 rounded-full transition hover:opacity-70"
     style={{ color: C.ink }} aria-label={label}>
    {ICON[icon]}
    <span className="text-[10px] uppercase font-mono tracking-wider">{label}</span>
  </a>
);

export default function BrochureViewer() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [muted, setMuted] = useState(true);
  const audio = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}/api/pollen/public/brochure/${slug}`);
        if (!r.ok) throw new Error();
        const d = await r.json();
        if (alive) {
          setData(d);
          document.title = `${d.title} · Pollen Dots`;
          const setMeta = (prop, val) => {
            let m = document.querySelector(`meta[property="${prop}"]`);
            if (!m) { m = document.createElement('meta'); m.setAttribute('property', prop); document.head.appendChild(m); }
            m.setAttribute('content', val);
          };
          const firstPhoto = (d.gallery || []).find(g => g.kind === 'photo' && g.url);
          const ogImage = firstPhoto ? `${API}${firstPhoto.url}` : `${SITE}/assets/images/welcome/bg.webp`;
          setMeta('og:title', `${d.title} — Pollen Dots`);
          setMeta('og:description', d.subtitle || 'Mindful travel experiences across India.');
          setMeta('og:image', ogImage);
          setMeta('og:type', 'website');
          setMeta('og:url', `${SITE}/api/og/brochure/${slug}`);
        }
      } catch {
        if (alive) setErr('This brochure isn’t available right now.');
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  // Auto-play music as soon as it's loaded — browsers permit autoplay only
  // when muted, so we start muted and surface a small "tap to unmute" pill.
  // When any embedded video starts playing, we pause the background music
  // so the two sources never compete. Resume when the video pauses/ends.
  useEffect(() => {
    const a = audio.current;
    if (!a || !data?.music_url) return;
    a.muted = true; a.volume = 0.32;
    a.play().catch(() => { /* autoplay blocked entirely — user can still tap */ });
  }, [data?.music_url]);

  const toggleSound = () => {
    const a = audio.current;
    if (!a) return;
    if (muted) {
      a.muted = false;
      a.play().then(() => setMuted(false)).catch(() => {});
    } else {
      a.muted = true;
      setMuted(true);
    }
  };

  // Duck (pause) the background music while a tile video is playing.
  const onVideoPlay = () => { if (audio.current && !audio.current.paused) audio.current.pause(); };
  const onVideoPause = () => {
    const a = audio.current;
    if (a && data?.music_url) a.play().catch(() => {});
  };

  // Share via the OG-rich endpoint so WhatsApp / iMessage / email all
  // pull the hero photo + title preview card. The endpoint itself
  // redirects real browsers to the SPA viewer, so customers still
  // land on the brochure page.
  //
  // WhatsApp on Android concatenates `text + " " + url` when both are
  // passed to navigator.share — that smushes the tagline and the URL
  // onto one line. We keep the URL INSIDE the text body (on its own
  // line, after a blank line) and skip the `url` field entirely. Chat
  // apps still scan the text for URLs and build the rich preview.
  const share = async () => {
    const url = `${SITE}/api/og/brochure/${slug}`;
    // The rich preview card already carries the cover photo, title and
    // subtitle — the message must not repeat any of it. One warm line,
    // then the URL on its own line.
    const isMem = data?.kind === 'memories';
    const text = (isMem
      ? 'Some journeys stay with us forever 🌸\nCome relive the moments with us:'
      : 'Sharing something special with you 🌸')
      + '\n\n' + url;
    try {
      if (navigator.share) {
        // No `url` field — prevents WhatsApp Android from appending it
        // to the last line of `text`. The URL inside `text` is still
        // detected and rendered as a rich preview card by WhatsApp /
        // iMessage / Telegram / Mail.
        await navigator.share({ text });
      } else {
        await navigator.clipboard.writeText(text);
        alert('Link copied');
      }
    } catch { /* user cancelled */ }
  };

  if (err) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: C.bg, color: C.ink }}>
      <p className="text-sm" style={{ fontFamily: 'Larken, Cormorant Garamond, Georgia, serif' }}>{err}</p>
    </div>
  );
  if (!data) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: C.bg }}>
      <Loader2 className="w-6 h-6 animate-spin" style={{ color: C.pine }} />
    </div>
  );

  const gallery = data.gallery || [];
  const heroIdx = Math.min(Math.max(0, data.hero_index || 0), Math.max(0, gallery.length - 1));
  const hero = gallery[heroIdx];
  const rest = gallery.filter((_, i) => i !== heroIdx);

  return (
    <div className="min-h-screen" style={{ background: C.bg, color: C.ink, fontFamily: 'Larken, Cormorant Garamond, Georgia, serif' }}>
      {data.music_url && <audio ref={audio} src={data.music_url} loop preload="auto" />}

      {/* Floating actions — Share + sound mute toggle (above sticky footer) */}
      <div className="fixed bottom-20 right-5 z-50 flex flex-col gap-2 items-end">
        <button onClick={share} data-testid="brochure-share"
                className="h-11 w-11 rounded-full shadow-lg flex items-center justify-center"
                style={{ background: C.maroon, color: C.bg }} aria-label="Share">
          <Share2 className="w-4 h-4" />
        </button>
        {data.music_url && (
          <button onClick={toggleSound} data-testid="brochure-music-toggle"
                  className="h-11 w-11 rounded-full shadow-lg flex items-center justify-center"
                  style={{ background: C.pine, color: C.bg }}
                  aria-label={muted ? 'Unmute music' : 'Mute music'}>
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
        )}
      </div>

      {/* Sticky top brand banner — logo + wordmark + tagline as ONE lockup
          that stays pinned while customers scroll the gallery. */}
      <BrandBanner />

      {/* Title block — tight */}
      <section className="max-w-3xl mx-auto px-6 pt-6 pb-4 text-center">
        <h1 className="text-2xl sm:text-4xl leading-tight" style={{ color: C.ink, fontWeight: 400 }}>
          {data.title}
        </h1>
        {data.subtitle && (
          <p className="mt-2 text-sm sm:text-base italic" style={{ color: C.muted }}>
            {data.subtitle}
          </p>
        )}
      </section>

      {/* Album cover — the operator-picked photo leads the page. */}
      {hero && hero.kind === 'photo' && hero.url && (
        <section className="max-w-5xl mx-auto px-4 pb-4" data-testid="brochure-cover">
          <div className="rounded-xl overflow-hidden" style={{ boxShadow: '0 6px 30px rgba(72,18,32,0.14)' }}>
            <img src={hero.url.startsWith('http') ? hero.url : `${API}${hero.url}`}
                 alt={hero.caption || data.title || 'Album cover'}
                 className="w-full h-auto block" loading="eager" />
          </div>
          {hero.caption && (
            <p className="mt-2 text-xs italic text-center" style={{ color: C.muted }}>{hero.caption}</p>
          )}
        </section>
      )}

      {/* Gallery — CSS masonry grid; native aspect ratios preserved, no
          cropping, no slides. Whole document scrolls for more media. */}
      {gallery.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 pb-6">
          <div className="columns-1 sm:columns-2 gap-3" data-testid="brochure-gallery">
            {(hero && hero.kind === 'photo' && hero.url ? rest : gallery).map((m, i) => (
              <div key={m.id || i} className="break-inside-avoid mb-3">
                <Media m={m} eager={i < 2} onVideoPlay={onVideoPlay} onVideoPause={onVideoPause} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Opening paragraph */}
      {data.intro && (
        <section className="max-w-2xl mx-auto px-6 pb-6">
          <p className="text-base leading-relaxed text-center" style={{ color: C.ink }}>
            {data.intro}
          </p>
        </section>
      )}

      {/* Hotel sections — rendered AFTER the loose gallery and intro so
          they read like a deeper chapter. Each hotel groups its own room
          categories, each with its own media grid. A trio of pollen
          flowers sits above every hotel as a quiet section break. */}
      {(data.hotels || []).length > 0 && (
        <section className="max-w-5xl mx-auto px-4 pb-8"
                 data-testid="brochure-hotels">
          {(data.hotels || []).map((h) => (
            <article key={h.id} className="space-y-4">
              <TripleFlowerDivider />
              <header className="text-center px-2">
                <p className="text-[10px] tracking-[0.25em] uppercase"
                   style={{ color: C.gold, letterSpacing: '0.25em' }}>
                  Hotel
                </p>
                <h2 className="text-2xl sm:text-3xl mt-1"
                    style={{ color: C.ink, fontWeight: 400,
                             fontFamily: 'Larken, Cormorant Garamond, Georgia, serif',
                             fontStyle: 'italic' }}>
                  {h.name || 'Hotel'}
                </h2>
                {h.intro && (
                  <p className="mt-1.5 text-sm italic max-w-xl mx-auto"
                     style={{ color: C.muted }}>
                    {h.intro}
                  </p>
                )}
              </header>
              {(h.rooms || []).map((r) => (
                <div key={r.id} className="space-y-2.5">
                  <div className="px-2">
                    <h3 className="text-base sm:text-lg" style={{ color: C.maroon, fontWeight: 600 }}>
                      {r.name || 'Room'}
                    </h3>
                    {r.description && (
                      <p className="text-xs sm:text-sm mt-0.5" style={{ color: C.muted }}>
                        {r.description}
                      </p>
                    )}
                  </div>
                  {(r.media || []).length > 0 ? (
                    <div className="columns-1 sm:columns-2 gap-3">
                      {(r.media || []).map((m, i) => (
                        <div key={m.id || i} className="break-inside-avoid mb-3">
                          <Media m={m} eager={false}
                                 onVideoPlay={onVideoPlay}
                                 onVideoPause={onVideoPause} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[11px] italic px-2" style={{ color: C.muted }}>
                      Photos coming soon.
                    </p>
                  )}
                </div>
              ))}
            </article>
          ))}
        </section>
      )}

      <div className="py-2"><PetalDivider /></div>

      {/* Bottom safe-area spacer so the sticky footer doesn't cover content */}
      <div className="h-20" />

      {/* Sticky footer — social row always visible while scrolling. */}
      <footer className="fixed bottom-0 left-0 right-0 z-40 backdrop-blur-md"
              style={{ background: `${C.bg}EE`, borderTop: `1px solid ${C.petal}66` }}>
        <div className="max-w-5xl mx-auto px-4 py-2 flex items-center justify-around">
          <Social href={SITE} icon="globe" label="Website" />
          <Social href="https://www.instagram.com/pollendots" icon="instagram" label="Instagram" />
          <Social href="https://wa.me/916295484375" icon="whatsapp" label="WhatsApp" />
          <Social href="mailto:travel@pollendots.com" icon="email" label="Email" />
        </div>
      </footer>
    </div>
  );
}
